import { useState } from "react"
import { router } from "@inertiajs/react"
import { Check, Clock, MessageSquare } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { ChapterOpening } from "@/components/book/ChapterOpening"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import logoBlackText from "@/assets/logos/kf-logo-black-text.webp"

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""
}

// A Profiled leader's private link to their chapter (no login). They read the
// chapter as their coach approved it, approve it once, and can send the coach
// a note. They never edit it.
export default function BookApprovalShow({ seo, book, chapter, version, token }) {
  const [note, setNote] = useState("")
  const [sending, setSending] = useState(false)
  const firstName = chapter.leaderName.split(/\s+/)[0]
  const coachFirst = book.coach.split(/\s+/)[0]

  const post = (path) => {
    setSending(true)
    router.post(`/book/${token}/${path}`, { note }, {
      preserveScroll: true,
      onSuccess: () => setNote(""),
      onFinish: () => setSending(false),
    })
  }

  return (
    <>
      <SeoHead seo={{ ...seo, noIndex: true }}>
        <meta name="robots" content="noindex, nofollow" />
      </SeoHead>
      <div className="min-h-screen bg-zinc-50">
        <header className="border-b bg-white">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
            <img src={logoBlackText} alt="Kingdom Factor" className="h-8 w-auto" />
            <span className="ml-auto text-sm text-zinc-600">{book.title}</span>
          </div>
        </header>

        <main className="mx-auto max-w-3xl space-y-6 px-4 py-8">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Hi {firstName}, here is your chapter</h1>
            <p className="text-zinc-600">
              {book.coach} wrote this chapter about you for <i>{book.title}</i>. Please read it. If it is right, approve it. If something needs to change, send {coachFirst} a note.
            </p>
          </div>

          {chapter.leaderApprovedAt && (
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <Check className="h-4 w-4" /> You approved this chapter on {formatDate(chapter.leaderApprovedAt)}. Thank you. You can still send {coachFirst} a note.
            </div>
          )}

          {version ? (
            <article className="kf-book-page rounded-lg border bg-white px-5 py-8 shadow-sm sm:px-10">
              <ChapterOpening {...chapter} title={version.title} subtitle={version.subtitle} />
              {version.sections.map((section) => (
                <section key={section.key}>
                  <h2>{section.heading}</h2>
                  {/* ChapterTemplate's HTML, sanitized on every write. */}
                  <div dangerouslySetInnerHTML={{ __html: section.body }} />
                </section>
              ))}
            </article>
          ) : (
            <div className="flex items-center gap-2 rounded-md border bg-white p-6 text-sm text-zinc-700">
              <Clock className="h-4 w-4" /> {book.coach} is making changes to your chapter. Come back soon, or send a note below.
            </div>
          )}

          <div className="space-y-3 rounded-lg border bg-white p-5">
            {!chapter.leaderApprovedAt && version && (
              <>
                <h2 className="text-lg font-semibold text-zinc-900">Is your chapter right?</h2>
                <Button size="lg" disabled={sending} onClick={() => post("approve")}>
                  <Check className="h-4 w-4" /> Yes, I approve my chapter
                </Button>
                <p className="text-xs text-zinc-500">You approve it once. {coachFirst} may still fix small things before the book is printed.</p>
              </>
            )}
            <h2 className="pt-2 text-lg font-semibold text-zinc-900">Send {coachFirst} a note</h2>
            <p className="text-sm text-zinc-600">For a wrong fact, a name, a date, or anything you want changed. {coachFirst} makes the change.</p>
            <Textarea rows={4} value={note} maxLength={chapter.noteMax} onChange={(e) => setNote(e.target.value)}
              placeholder={`For example: My father's name is spelled Earl, not Earle.`} />
            <Button variant="outline" disabled={!note.trim() || sending} onClick={() => post("note")}>
              <MessageSquare className="h-4 w-4" /> Send the note
            </Button>
            {chapter.leaderNote && (
              <p className="text-xs text-zinc-500">Your last note, {formatDate(chapter.leaderNoteAt)}: “{chapter.leaderNote}”</p>
            )}
          </div>
        </main>
      </div>
    </>
  )
}
